import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import NotebookPanel from "@/components/NotebookPanel";
import NotebookTemplates from "@/components/NotebookTemplates";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Link2, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function Notebooks() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [repairing, setRepairing] = useState(false);
  const [classes, setClasses] = useState([]);
  const [groupId, setGroupId] = useState(params.get("group") || "");
  const [teacherId, setTeacherId] = useState("all");
  const isSuper = user?.role === "superadmin";
  const teachers = [...new Map(classes.filter((c) => c.teacher_id)
    .map((c) => [c.teacher_id, { id: c.teacher_id, name: c.teacher_name }])).values()];
  const visibleClasses = teacherId === "all" ? classes : classes.filter((c) => c.teacher_id === teacherId);

  useEffect(() => {
    api.get("/drive/status").then((r) => setStatus(r.data)).catch(() => setStatus({ connected: false }));
    api.get("/notebook/mine").then((r) => setClasses(r.data || []));
    if (params.get("drive") === "ok") toast.success("Google Drive conectado");
    if (params.get("drive") === "error") toast.error("No se pudo conectar Google Drive");
    if (params.get("drive") === "scope") toast.error("Faltó aceptar el permiso de Google Drive");
    if (params.get("drive") === "norefresh") toast.error("Google no devolvió el permiso permanente. Quitá el acceso en myaccount.google.com/permissions y volvé a conectar.");
    /* eslint-disable-next-line */
  }, []);

  useEffect(() => {
    setParams(groupId ? { group: groupId } : {}, { replace: true });
    /* eslint-disable-next-line */
  }, [groupId]);

  const connect = async () => {
    try {
      const r = await api.get("/drive/connect");
      window.location.href = r.data.authorization_url;
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo iniciar la conexión"); }
  };

  const disconnect = async () => {
    if (!window.confirm("¿Desconectar el Drive de la escuela? Los archivos no se borran.")) return;
    await api.delete("/drive/disconnect");
    const r = await api.get("/drive/status");
    setStatus(r.data);
  };

  const repairSharing = async () => {
    setRepairing(true);
    try {
      const r = await api.post("/notebook/repair-sharing");
      const d = r.data;
      if (d.failed) {
        toast.error(`Se abrieron ${d.folders_opened} carpetas y ${d.files_opened} documentos, pero ${d.failed} fallaron. ${d.errors?.[0] || ""}`);
      } else {
        toast.success(`Listo: ${d.folders_opened} carpetas y ${d.files_opened} documentos abiertos por enlace (de ${d.folders} y ${d.files} revisados)`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo arreglar");
    } finally {
      setRepairing(false);
    }
  };

  const current = classes.find((c) => c.id === groupId);

  return (
    <div>
      <PageHeader
        title={current ? `Notebook · ${current.name}` : "Notebooks"}
        subtitle="Una carpeta de Google Drive por clase: la ven el profe y todos sus estudiantes"
        testId="notebooks-header"
      />

      {isSuper && (
        <Card className="p-4 mb-5 flex flex-wrap items-center gap-3" data-testid="drive-status-card">
          {status?.connected ? (
            <>
              <CheckCircle2 className="h-5 w-5 text-emerald-600" />
              <span className="text-sm text-slate-700">
                Drive conectado como <strong data-testid="drive-email">{status.email || "cuenta de la escuela"}</strong>
              </span>
              <Button size="sm" variant="outline" onClick={disconnect} data-testid="drive-disconnect">Desconectar</Button>
              <Button size="sm" variant="ghost" onClick={repairSharing} disabled={repairing}
                      title="Abre el acceso por enlace de todas las carpetas y documentos, para que nadie tenga que solicitar permiso"
                      data-testid="drive-repair-sharing">
                {repairing ? "Arreglando…" : "Arreglar permisos"}
              </Button>
            </>
          ) : (
            <>
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              <span className="text-sm text-slate-700">
                Conectá la cuenta de Google de la escuela para crear los notebooks.
              </span>
              <Button size="sm" onClick={connect} disabled={!status?.configured} data-testid="drive-connect">
                <Link2 className="h-4 w-4" /> Conectar Google Drive
              </Button>
            </>
          )}
        </Card>
      )}

      <NotebookTemplates />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl mb-5">
        <div>
          <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Profesor</p>
          <Select value={teacherId} onValueChange={(v) => { setTeacherId(v); setGroupId(""); }}>
            <SelectTrigger data-testid="notebook-teacher-select"><SelectValue placeholder="Todos los profesores" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los profesores</SelectItem>
              {teachers.map((tt) => <SelectItem key={tt.id} value={tt.id}>{tt.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Programa / clase</p>
          <Select value={groupId} onValueChange={setGroupId}>
            <SelectTrigger data-testid="notebook-group-select"><SelectValue placeholder="Elegí una clase" /></SelectTrigger>
            <SelectContent>
              {visibleClasses.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {!groupId ? (
        <Card className="p-10 text-center text-slate-500" data-testid="notebooks-pick-hint">
          Elegí una clase para abrir su notebook
        </Card>
      ) : (
        <NotebookPanel key={groupId} groupId={groupId} />
      )}
    </div>
  );
}
